import { useLazyQuery } from '@apollo/client';
import Cookies from 'js-cookie';
import { useCallback, useEffect } from 'react';

import { UserIdDocument as GET_SCRAMBLED_ID } from './user-id.gql-types';

// V3 Usercentrics API types
type ConsentDetails = {
  consent: {
    status: 'ALL_ACCEPTED' | 'ALL_DENIED' | 'SOME_ACCEPTED' | 'SOME_DENIED';
  };
};

declare global {
  interface Window {
    UC_UI?: {
      areAllConsentsAccepted: () => boolean;
    };
    __ucCmp?: {
      getConsentDetails: () => Promise<ConsentDetails>;
    };
  }
}

// Runtime detection: check consent status using V3 or V2 API
const checkAllConsentsAccepted = async (): Promise<boolean> => {
  // V3 API (async) - check first as UC_UI is also present in V3
  if (window.__ucCmp?.getConsentDetails) {
    try {
      const consentDetails = await window.__ucCmp.getConsentDetails();
      return consentDetails?.consent.status === 'ALL_ACCEPTED';
    } catch {
      return false;
    }
  }
  // V2 API (sync) - fallback
  if (window.UC_UI?.areAllConsentsAccepted) {
    return window.UC_UI.areAllConsentsAccepted();
  }
  return false;
};

export const useScrambledId = (): void => {
  const [fetchScrambledId] = useLazyQuery(GET_SCRAMBLED_ID, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.viewer?.id) {
        Cookies.set('s_id2', data?.viewer?.id, {
          expires: 30,
          domain: 'xing.com',
        });
      }
    },
  });

  const handleInitializedEvent = useCallback((): void => {
    void (async () => {
      try {
        const hasMarketingConsent = await checkAllConsentsAccepted();

        if (Cookies.get('s_id2') && !hasMarketingConsent) {
          Cookies.remove('s_id2', { domain: 'xing.com' });
        }

        if (!Cookies.get('s_id2') && hasMarketingConsent) {
          fetchScrambledId();
        }
      } catch (error) {
        console.error('Failed to get consent details:', error);
      }
    })();
  }, [fetchScrambledId]);

  useEffect(() => {
    window.addEventListener('UC_UI_INITIALIZED', handleInitializedEvent);

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', handleInitializedEvent);
    };
  }, [handleInitializedEvent]);
};
